<template>
  <div
    class="cn-header-mobile"
    :class="{
      'cn-header-mobile--open': isMobileMenuOpen,
    }"
  >
    <div class="cn-header-mobile__block">
      <div class="cn-header-mobile__container container">
        <div class="cn-header-mobile__top">
          <div class="cn-header-mobile__logo">
            <UiLink to="/" aria-label="Canyon main page">
              <img
                :src="isMobileMenuOpen ? logoLight || logo : logo"
                loading="lazy"
                width="144"
                height="24"
                alt="Canyon logo"
              />
            </UiLink>
          </div>
          <div class="cn-header-mobile__controls">
            <button
              v-if="isMobileMenuOpen"
              class="cn-header-mobile__search-button"
              aria-label="Open search"
              @click="toggleSearch"
            >
              <UiIcon name="search" />
            </button>
            <button
              class="cn-header-mobile__open-button"
              aria-label="Open menu"
              @click="toggleMobileMenu"
            >
              <UiIcon v-if="!isMobileMenuOpen" name="menu" />
              <UiIcon v-else name="close" />
            </button>
          </div>
        </div>
        <div class="cn-header-mobile__bottom">
          <AppHeaderMobileMenu v-model="isMobileMenuOpen" :menu="menu" />
          <AppHeaderMobileSearch v-model="isSearchOpen" @search="close" />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue';
import { useBodyScrollLock } from '@/composables';
import AppHeaderMobileSearch from '@/components/AppHeaderMobileSearch';
import AppHeaderMobileMenu from '@/components/AppHeaderMobileMenu';
import UiLink from '@/components/ui/UiLink';
import UiIcon from '@/components/ui/UiIcon';

const { logo, logoLight } = useRuntimeConfig().public.frontkit;

interface Props {
  menu: any[];
}

defineProps<Props>();

const isMobileMenuOpen = ref(false);
useBodyScrollLock(() => isMobileMenuOpen.value);
const toggleMobileMenu = () => {
  isMobileMenuOpen.value = !isMobileMenuOpen.value;
};

const close = () => {
  isMobileMenuOpen.value = false;
};

const isSearchOpen = ref(false);
const toggleSearch = () => {
  isSearchOpen.value = !isSearchOpen.value;
};

const route = useRoute();
watch(
  () => route.path,
  () => {
    close();
  },
);
</script>
<style lang="scss">
.cn-header-mobile {
  --ab-canyon-header-mobile-block-color-bg: var(--ab-canyon-color-light);
  --ab-canyon-header-mobile-block-color-bg-open: var(--ab-canyon-color-primary);
  --ab-canyon-header-mobile-block-color-buttons-text-ligth: var(
    --ab-canyon-color-light
  );
  --ab-canyon-header-mobile-block-color-buttons-text-dark: var(
    --ab-canyon-color-dark
  );
  z-index: 30;
  top: 0px;
  right: 0px;
  left: 0px;
  position: fixed;

  &__block {
    background-color: var(--ab-canyon-header-mobile-block-color-bg);
    position: relative;
    height: 100%;
  }
  &__container {
    flex-direction: column;
    height: 100%;
    display: flex;
  }
  &__logo {
    width: calc(9 * var(--size-base));
  }
  &__controls {
    align-items: center;
    display: flex;
  }
  &__open-button {
    color: var(--ab-canyon-header-mobile-block-color-buttons-text-dark);
    @media (min-width: 1656px) {
      display: none;
    }

    .cn-icon {
      width: var(--size-xl);
      height: var(--size-xl);
    }
  }
  &__search-button {
    color: var(--ab-canyon-header-mobile-block-color-buttons-text-ligth);
    margin-right: var(--size-xl);
    .cn-icon {
      width: var(--size-xl);
      height: var(--size-xl);
    }
  }
  &__top {
    justify-content: space-between;
    align-items: center;
    flex-shrink: 0;
    height: 64px;
    display: flex;
  }
  &__bottom {
    overflow: auto;
    flex-grow: 1;
    @media (min-width: 1656px) {
      display: none;
    }
  }

  &--open {
    height: 100vh;
    .cn-header-mobile {
      &__block {
        background-color: var(--ab-canyon-header-mobile-block-color-bg-open);
      }
      &__open-button {
        color: var(--ab-canyon-header-mobile-block-color-buttons-text-ligth);
      }
    }
  }
}
</style>
